// ClientCreateDialog.js

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import API from '../../services/api';

export default function ClientCreateDialog({ open, onClose, onClientCreated, userProfile }) {
  const queryClient = useQueryClient();
  const [createStep, setCreateStep] = useState('clientDetails');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (createError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [createError]);

  // Read the config from userProfile
  const uiConfig = userProfile?.ui_config || {};
  const clientFields = uiConfig.client_fields || [];
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const debtFields = uiConfig.debt_fields || [];
  const debtFieldsSchema = uiConfig.debt_fields_schema || {};
  
  // Get the enforce_id flag from UI config
  // Default to true if the field doesn't exist
  const enforceIdValidation = uiConfig.enforce_id !== false;
  // Check if auto-generation of client IDs is enabled
  const autoGenerateId = uiConfig.auto_generate_id === true;

  // Group-related config
  const includeGroups = uiConfig.include_groups === true;
  const allowedGroupTypes = uiConfig.group_types || [];
  const groupsSchemas = uiConfig.groups_schemas || {};

  const [clientFormData, setClientFormData] = useState(() => {
    const init = {};
    clientFields.forEach((f) => {
      init[f] = '';
    });
    return init;
  });

  // Set default value "0" for each debt field (except TotalDebt)
  const [debtFormData, setDebtFormData] = useState(() => {
    const init = {};
    debtFields.forEach((f) => {
      if (f !== 'TotalDebt') {
        init[f] = '0';
      }
    });
    return init;
  });

  // Group selection state
  const [groupFormData, setGroupFormData] = useState({
    Flow: null,
    Year: null,
    General: [],
  });

  // Fetch available groups when dialog is open and groups feature is enabled
  const { data: availableGroups = [], isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await API.get('/groups/');
      return response.data;
    },
    enabled: open && includeGroups,
    staleTime: 0, // Always refetch when dialog opens to get latest groups
  });

  // Group groups by type for easy access
  const groupsByType = React.useMemo(() => {
    const result = {};
    allowedGroupTypes.forEach((type) => {
      result[type] = availableGroups.filter((g) => g.GroupType === type);
    });
    return result;
  }, [availableGroups, allowedGroupTypes]);

  // Helper to get group type label
  const getGroupTypeLabel = (groupType) => {
    return groupsSchemas[groupType]?.label || groupType;
  };

  // =========== CREATE MUTATION (HARD REFRESH) ===========
  const createClientMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await API.post('/client/create/', payload);
      return response.data;
    },
    onSuccess: () => {
      // discard local data and re-fetch from server
      queryClient.invalidateQueries(['clients']);
      // Actively refetch the query if it's active
      queryClient.refetchQueries(['clients'], { active: true });
      // Reset pagination to page 0 so user sees the new client
      if (onClientCreated) {
        onClientCreated();
      }
    },
    onError: (err) => {
      console.error('Error creating client:', err);
    },
  });

  // If the dialog re-opens, reset the form
  useEffect(() => {
    if (open) {
      setCreateStep('clientDetails');
      setCreateError('');
      setCreateSuccess('');

      const initClient = {};
      clientFields.forEach((f) => (initClient[f] = ''));
      setClientFormData(initClient);

      const initDebt = {};
      debtFields.forEach((f) => {
        if (f !== 'TotalDebt') {
          initDebt[f] = '0';
        }
      });
      setDebtFormData(initDebt);

      // Reset group selections
      setGroupFormData({
        Flow: null,
        Year: null,
        General: [],
      });
    }
  }, [open]);

  // =========== STEPS ===========
  const handleClientDetailsNext = () => {
    setCreateError('');
    for (let f of clientFields) {
      // Skip all validation for ClientBusinessID when auto-generate is enabled
      if (f === 'ClientBusinessID' && autoGenerateId) continue;

      const value = (clientFormData[f] || '').trim();
      const schema = clientFieldsSchema[f] || {};
      if (schema.required && !value) {
        setCreateError(`שדה "${schema.label || f}" הוא שדה חובה`);
        return;
      }

      // For ClientBusinessID, validate format
      if (f === 'ClientBusinessID') {

        // Check that the value contains only digits
        if (!/^\d*$/.test(value)) {
          setCreateError('מזהה חייב להכיל ספרות בלבד');
          return;
        }

        // Only validate ID length if enforce_id is true
        if (enforceIdValidation && value.length !== 9) {
          setCreateError('חפ/תז חייב להיות 9 ספרות');
          return;
        }
      }
    }
    setCreateStep('debtDetails');
  };

  const handleDebtNext = () => {
    setCreateError('');
    if (includeGroups) {
      setCreateStep('groupSelection');
    } else {
      setCreateStep('confirm');
    }
  };

  const handleGroupSelectionNext = () => {
    setCreateError('');
    setCreateStep('confirm');
  };

  const handleCreateClient = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setCreateError('');
    setCreateSuccess('');

    try {
      const debtPayload = {};
      debtFields.forEach((f) => {
        if (f !== 'TotalDebt') {
          const rawVal = (debtFormData[f] || '').trim();
          const numericVal = parseFloat(rawVal.replace(',', '.'));
          debtPayload[f] = isNaN(numericVal) ? 0 : numericVal;
        }
      });

      // Build client payload, converting empty date strings to null
      const clientPayload = {};
      clientFields.forEach((fieldKey) => {
        // Skip ClientBusinessID when auto-generate is enabled (backend will generate it)
        if (fieldKey === 'ClientBusinessID' && autoGenerateId) return;

        const schema = clientFieldsSchema[fieldKey] || {};
        const value = clientFormData[fieldKey];

        // For date fields, send null instead of empty string
        if (schema.type === 'date') {
          clientPayload[fieldKey] = value || null;
        } else {
          clientPayload[fieldKey] = value;
        }
      });

      const payload = {
        ...clientPayload,
        debt: debtPayload,
      };

      // Add group IDs if groups feature is enabled and selections were made
      if (includeGroups) {
        const groupIds = [];
        if (groupFormData.Flow) {
          groupIds.push(groupFormData.Flow.GroupID);
        }
        if (groupFormData.Year) {
          groupIds.push(groupFormData.Year.GroupID);
        }
        groupFormData.General.forEach((g) => {
          groupIds.push(g.GroupID);
        });

        if (groupIds.length > 0) {
          payload.group_ids = groupIds;
        }
      }

      await createClientMutation.mutateAsync(payload);
      setCreateSuccess('הלקוח נוצר בהצלחה!');
      setCreateStep('done');
    } catch (err) {
      let errorMsg =
        err?.response?.data?.error ||
        err?.response?.data?.ClientBusinessID ||
        err?.response?.data?.ClientName ||
        'שגיאה ביצירת לקוח חדש';

      if (errorMsg.includes('client deleted')) {
        errorMsg = 'לקוח נמצא במערכת אנא שחזר אותו בבקשה מרישימת לקוחות מחוקים';
      } else if (errorMsg.includes('already exists')) {
        errorMsg = ` ${clientFieldsSchema['ClientBusinessID'].label || 'מזהה לקוח'} זה כבר קיים`;
      } else if (errorMsg === 'Business ID must be exactly 9 digits.') {
        errorMsg = 'חפ/תז חייב להיות באורך 9 ספרות בדיוק';
      }
      setCreateError(errorMsg);
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // =========== RENDERING ===========
  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose?.();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.8rem', fontWeight: 'bold', mb: 1 }}>
        יצירת לקוח חדש
      </DialogTitle>

      <DialogContent dividers sx={{ fontSize: '1.2rem' }}>
        {createError && <Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
        {createSuccess && <Alert severity="success" sx={{ mb: 2 }}>{createSuccess}</Alert>}

        {/* STEP 1: CLIENT DETAILS */}
        {createStep === 'clientDetails' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
          {clientFields.map((fieldKey) => {
            const schema = clientFieldsSchema[fieldKey] || {};

            // Skip rendering read-only fields completely
            if (schema.readonly === true) {
              return null;
            }

            // Hide ClientBusinessID when auto-generate is enabled
            if (fieldKey === 'ClientBusinessID' && autoGenerateId) {
              return null;
            }

            const labelText = schema.required
              ? (schema.label || fieldKey)
              : `${schema.label || fieldKey} (אופציונאלי)`;

            // Render DatePicker for date-type fields
            if (schema.type === 'date') {
              return (
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={he}
                  key={fieldKey}
                  localeText={{
                    cancelButtonLabel: 'ביטול',
                    okButtonLabel: 'אישור',
                    clearButtonLabel: 'נקה',
                    todayButtonLabel: 'היום',
                  }}
                >
                  <DatePicker
                    label={labelText}
                    value={clientFormData[fieldKey] ? new Date(clientFormData[fieldKey]) : null}
                    onChange={(newValue) => {
                      // Store as local YYYY-MM-DD for API (not toISOString,
                      // which shifts to UTC and yields the previous day for Israel users).
                      const dateStr = newValue
                        ? `${newValue.getFullYear()}-${String(newValue.getMonth() + 1).padStart(2, '0')}-${String(newValue.getDate()).padStart(2, '0')}`
                        : '';
                      setClientFormData((prev) => ({ ...prev, [fieldKey]: dateStr }));
                    }}
                    disableFuture={fieldKey === 'BirthDate'}
                    maxDate={fieldKey === 'BirthDate' ? new Date() : undefined}
                    shouldDisableDate={(date) => {
                      if (fieldKey === 'BirthDate') {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        return date > today;
                      }
                      return false;
                    }}
                    sx={{direction: 'ltr'}}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: schema.required,
                      },
                    }}
                    format="dd/MM/yyyy"
                  />
                </LocalizationProvider>
              );
            }

            const inputType = schema.type === 'number' ? 'text' : (schema.type || 'text');

            // Add appropriate helper text for ClientBusinessID
            const helperText = fieldKey === 'ClientBusinessID'
              ? enforceIdValidation
                ? 'חפ/תז חייב להיות 9 ספרות ולהכיל ספרות בלבד'
                : 'מזהה חייב להכיל ספרות בלבד'
              : '';

            return (
              <TextField
                key={fieldKey}
                label={labelText}
                type={inputType}
                required={schema.required}
                fullWidth
                value={clientFormData[fieldKey]}
                onChange={(e) =>
                  setClientFormData((prev) => ({ ...prev, [fieldKey]: e.target.value }))
                }
                helperText={helperText}
              />
            );
          })}
        </Box>
        )}

        {/* STEP 2: DEBT DETAILS */}
        {createStep === 'debtDetails' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              חוב התחלתי (אופציונלי):
            </Typography>
            {debtFields
              .filter((f) => f !== 'TotalDebt')
              .map((fieldKey) => {
                const schema = debtFieldsSchema[fieldKey] || {};
                const labelText = schema.label || fieldKey;

                return (
                  <TextField
                    key={fieldKey}
                    label={labelText}
                    type="number"
                    fullWidth
                    value={debtFormData[fieldKey]}
                    onChange={(e) =>
                      setDebtFormData((prev) => ({ ...prev, [fieldKey]: e.target.value }))
                    }
                    onFocus={(e) => e.target.select()}
                    sx={{
                      direction: 'ltr',
                      '& input': {
                        textAlign: 'right',
                        // Remove spinner arrows in Chrome, Safari, Edge, Opera
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        // Remove spinner arrows in Firefox
                        '-moz-appearance': 'textfield',
                      },
                    }}
                    margin="normal"
                  />
                );
              })}
          </Box>
        )}

        {/* STEP 3: GROUP SELECTION (only if include_groups is enabled) */}
        {createStep === 'groupSelection' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              שיוך לקבוצות (אופציונלי):
            </Typography>

            {isLoadingGroups ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Flow Group - Single Select */}
                {allowedGroupTypes.includes('Flow') && (
                  <Autocomplete
                    options={groupsByType['Flow'] || []}
                    getOptionLabel={(option) => option.GroupName || ''}
                    value={groupFormData.Flow}
                    onChange={(_, newValue) =>
                      setGroupFormData((prev) => ({ ...prev, Flow: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`${getGroupTypeLabel('Flow')} (אופציונלי)`}
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.GroupID === value?.GroupID
                    }
                    noOptionsText="אין קבוצות זמינות"
                  />
                )}

                {/* Year Group - Single Select */}
                {allowedGroupTypes.includes('Year') && (
                  <Autocomplete
                    options={groupsByType['Year'] || []}
                    getOptionLabel={(option) => option.GroupName || ''}
                    value={groupFormData.Year}
                    onChange={(_, newValue) =>
                      setGroupFormData((prev) => ({ ...prev, Year: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`${getGroupTypeLabel('Year')} (אופציונלי)`}
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.GroupID === value?.GroupID
                    }
                    noOptionsText="אין קבוצות זמינות"
                  />
                )}

                {/* General Groups - Multi Select with Checkboxes */}
                {allowedGroupTypes.includes('General') && (
                  <Autocomplete
                    multiple
                    options={groupsByType['General'] || []}
                    getOptionLabel={(option) => option.GroupName || ''}
                    value={groupFormData.General}
                    onChange={(_, newValue) =>
                      setGroupFormData((prev) => ({ ...prev, General: newValue }))
                    }
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => {
                      const { key, ...restProps } = props;
                      return (
                        <li key={key} {...restProps}>
                          <Checkbox
                            checked={selected}
                            sx={{ mr: 1 }}
                          />
                          {option.GroupName}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`${getGroupTypeLabel('General')} (אופציונלי)`}
                        fullWidth
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option?.GroupID === value?.GroupID
                    }
                    noOptionsText="אין קבוצות זמינות"
                  />
                )}
              </>
            )}
          </Box>
        )}

        {/* STEP 4: CONFIRM DETAILS */}
        {createStep === 'confirm' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              אישור לפני יצירה
            </Typography>
            {clientFields.map((f) => {
              const schema = clientFieldsSchema[f] || {};

              // Skip rendering read-only fields in summary
              if (schema.readonly === true) {
                return null;
              }

              // Skip ClientBusinessID in summary when auto-generated
              if (f === 'ClientBusinessID' && autoGenerateId) {
                return null;
              }

              const label = schema.label || f;
              // Format date fields for display
              let displayValue = clientFormData[f];
              if (schema.type === 'date' && displayValue) {
                displayValue = new Date(displayValue).toLocaleDateString('he-IL', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                });
              }
              return (
                <Typography key={f}>
                  {label}: {displayValue}
                </Typography>
              );
            })}

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 'bold' }}>חוב התחלתי:</Typography>
              {debtFields
                .filter((f) => f !== 'TotalDebt')
                .map((f) => {
                  const label = debtFieldsSchema[f]?.label || f;
                  return (
                    <Typography key={f}>
                      {label}: {debtFormData[f]}
                    </Typography>
                  );
                })}
            </Box>

            {/* Group selections display */}
            {includeGroups && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 'bold' }}>קבוצות:</Typography>
                {groupFormData.Flow && (
                  <Typography>
                    {getGroupTypeLabel('Flow')}: {groupFormData.Flow.GroupName}
                  </Typography>
                )}
                {groupFormData.Year && (
                  <Typography>
                    {getGroupTypeLabel('Year')}: {groupFormData.Year.GroupName}
                  </Typography>
                )}
                {groupFormData.General.length > 0 && (
                  <Typography>
                    {getGroupTypeLabel('General')}: {groupFormData.General.map((g) => g.GroupName).join(', ')}
                  </Typography>
                )}
                {!groupFormData.Flow && !groupFormData.Year && groupFormData.General.length === 0 && (
                  <Typography color="text.secondary">לא נבחרו קבוצות</Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* STEP 5: DONE (success) */}
        {createStep === 'done' && (
          <Typography sx={{ fontWeight: 'bold', my: 2 }}>
            הלקוח נוצר בהצלחה! ניתן לסגור חלון זה.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        {createStep !== 'done' && (
          <Button
            onClick={onClose}
            color="inherit"
            sx={{ fontSize: '1.1rem' }}
            disabled={isSubmitting}
          >
            ביטול
          </Button>
        )}

        {createStep === 'clientDetails' && (
          <Button
            variant="contained"
            onClick={handleClientDetailsNext}
            sx={{ fontSize: '1.1rem' }}
          >
            הבא
          </Button>
        )}

        {createStep === 'debtDetails' && (
          <>
            <Button
              color="inherit"
              onClick={() => setCreateStep('clientDetails')}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={handleDebtNext}
              sx={{ fontSize: '1.1rem' }}
            >
              הבא
            </Button>
          </>
        )}

        {createStep === 'groupSelection' && (
          <>
            <Button
              color="inherit"
              onClick={() => setCreateStep('debtDetails')}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={handleGroupSelectionNext}
              sx={{ fontSize: '1.1rem' }}
            >
              הבא
            </Button>
          </>
        )}

        {createStep === 'confirm' && (
          <>
            <Button
              color="inherit"
              onClick={() => setCreateStep(includeGroups ? 'groupSelection' : 'debtDetails')}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateClient}
              sx={{ fontSize: '1.1rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'אישור סופי'}
            </Button>
          </>
        )}

        {createStep === 'done' && (
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ fontSize: '1.1rem' }}
          >
            סגור
          </Button>
        )}
      </DialogActions>
    </MobileDialog>
  );
}
